import { useState, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { clearTileSelection } from '@/features/ui/uiSlice';
import { assignAssetToSections } from '@/features/sections/sectionsSlice';
import styles from './ClipSelectionBar.module.css';

export default function ClipSelectionBar() {
  const dispatch = useAppDispatch();
  const selectedIds = useAppSelector((s) => s.ui.selectedTileIds);
  const sections = useAppSelector((s) => s.sections.library);
  const count = selectedIds.length;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Only non-dynamic sections can have assets manually assigned
  const assignableSections = sections.filter((s) => !s.is_dynamic);
  const selectedSection = assignableSections.find((s) => s.id === selectedSectionId);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  if (count === 0) return null;

  const handleAddToCollection = () => {
    if (!selectedSectionId) return;
    for (const assetId of selectedIds) {
      dispatch(assignAssetToSections({ assetId, payload: { section_ids: [selectedSectionId] } }));
    }
    dispatch(clearTileSelection());
    setSelectedSectionId(null);
  };

  return (
    <div className={styles.bar} role="toolbar" aria-label="Clip selection actions">
      {/* Left: count */}
      <div className={styles.left}>
        <span className={styles.count}>{count}</span>
        <span className={styles.countLabel}>{count === 1 ? 'clip' : 'clips'} selected</span>
      </div>

      {/* Right: collection selector + action buttons + clear */}
      <div className={styles.right}>
        {/* Collection dropdown */}
        <div className={styles.dropdownWrap} ref={dropdownRef}>
          <button
            className={styles.projectSelect}
            onClick={() => setDropdownOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <path d="M8 21h8M12 17v4" />
            </svg>
            <span className={styles.selectLabel}>{selectedSection ? selectedSection.label : 'Select a collection'}</span>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {dropdownOpen && (
            <ul className={styles.dropdown} role="listbox" aria-label="Collections">
              {assignableSections.length === 0 && (
                <li className={styles.dropdownEmpty}>No collections available</li>
              )}
              {assignableSections.map((sec) => (
                <li
                  key={sec.id}
                  className={`${styles.dropdownItem} ${sec.id === selectedSectionId ? styles.dropdownItemActive : ''}`}
                  role="option"
                  aria-selected={sec.id === selectedSectionId}
                  onClick={() => { setSelectedSectionId(sec.id); setDropdownOpen(false); }}
                >
                  {sec.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Add to collection */}
        <button
          className={`${styles.addBtn} ${selectedSectionId ? styles.addBtnActive : ''}`}
          disabled={!selectedSectionId}
          onClick={handleAddToCollection}
          title="Add to collection"
        >
          <span className={styles.addLabel}>Add to collection</span>
          <svg className={styles.addIcon} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>

        {/* Story Builder — coming soon */}
        <button className={`${styles.actionBtn} ${styles.actionBtnDisabled}`} disabled aria-disabled="true" title="Story Builder (coming soon)">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
          <span className={styles.btnLabel}>Story Builder</span>
        </button>

        {/* Video Editor — coming soon */}
        <button className={`${styles.actionBtn} ${styles.actionBtnDisabled}`} disabled aria-disabled="true" title="Video Editor (coming soon)">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
          <span className={styles.btnLabel}>Video Editor</span>
        </button>

        {/* Premiere Pro — coming soon */}
        <button className={`${styles.actionBtn} ${styles.actionBtnDisabled} ${styles.actionBtnPremiere}`} disabled aria-disabled="true" title="Export to Premiere Pro (coming soon)">
          {/* Pr monogram — matches the official Premiere Pro logomark */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect width="24" height="24" rx="4" fill="#9999FF" opacity="0.18" />
            <text x="4" y="18" fontFamily="Arial,sans-serif" fontWeight="700" fontSize="13" fill="#9999FF">Pr</text>
          </svg>
          <span className={styles.btnLabel}>Premiere Pro</span>
        </button>

        <button
          className={styles.clearBtn}
          onClick={() => dispatch(clearTileSelection())}
          aria-label="Clear selection"
          title="Clear selection"
        >
          <svg className={styles.clearIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <span className={styles.clearLabel}>Clear</span>
        </button>
      </div>
    </div>
  );
}
